import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCRM } from '../context/CRMContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import ClientForm from '../components/Forms/ClientForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiMail, FiPhone, FiMapPin, FiUsers, FiGlobe, FiCreditCard, FiArrowRight } = FiIcons;

const Clients = () => {
  const { clients, addClient, updateClient, deleteClient } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const handleAddClient = () => {
    setEditingClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingClient) {
      updateClient(editingClient.id, data);
    } else {
      addClient(data);
    }
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleDeleteClient = (id) => {
    if (window.confirm('Are you sure you want to delete this client? This will also delete all associated contracts.')) {
      deleteClient(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
        <Button onClick={handleAddClient}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          Add Client
        </Button>
      </div>

      {clients.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiUsers} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients yet</h3>
          <p className="text-gray-500 mb-4">Get started by adding your first client.</p>
          <Button onClick={handleAddClient}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={`/clients/${client.id}`}>
                <Card className="p-6 hover:shadow-lg transition-shadow h-full flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{client.name}</h3>
                      {client.company && (
                        <p className="text-sm text-gray-600">{client.company}</p>
                      )}
                    </div>
                    <div className="flex space-x-1 z-10">
                      <button
                        onClick={(e) => { e.preventDefault(); handleEditClient(client); }}
                        className="text-gray-400 hover:text-primary-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                        title="Edit Client"
                      >
                        <SafeIcon icon={FiEdit} className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.preventDefault(); handleDeleteClient(client.id); }}
                        className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                        title="Delete Client"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 flex-grow">
                    {client.email && (
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiMail} className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{client.email}</span>
                      </div>
                    )}
                    {client.phone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiPhone} className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                    {client.country && (
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiGlobe} className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>{client.country}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                    <p className="text-xs text-gray-500">
                      Added {format(new Date(client.createdAt), 'MMM dd, yyyy')}
                    </p>
                    <div className="flex items-center text-sm text-primary-600 font-medium">
                      View Profile
                      <SafeIcon icon={FiArrowRight} className="h-4 w-4 ml-1" />
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingClient ? 'Edit Client' : 'Add New Client'}
        size="lg"
      >
        <ClientForm
          client={editingClient}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default Clients;